import React from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
} from "@mui/material";

// Standard page wrapper: consistent padding, optional centered max width.
export function PageContainer({ maxWidth, children, sx }) {
    return (
        <Box sx={{ padding: 3, ...(maxWidth ? { maxWidth, margin: "0 auto" } : {}), textAlign: "left", ...sx }}>
            {children}
        </Box>
    );
}

// Title row with right-aligned actions.
export function SectionHeader({ title, children }) {
    return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5">{title}</Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>{children}</Box>
        </Box>
    );
}

// Compact metric card: big value over a small label, optional secondary/tertiary lines.
export function StatCard({ label, value, secondary, tertiary, tertiaryColor }) {
    return (
        <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: "center" }}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>{label}</Typography>
            <Typography variant="h5">{value}</Typography>
            {secondary && <Typography variant="body2" color="text.secondary">{secondary}</Typography>}
            {tertiary && <Typography variant="body2" color={tertiaryColor ?? "text.secondary"}>{tertiary}</Typography>}
        </Paper>
    );
}

// Null/0-safe date display; accepts epoch ms or anything Date() parses.
export function formatDate(value) {
    return value ? new Date(value).toLocaleString() : "";
}

// Relative time ("just now", "3 min ago", "2 h ago", "5 d ago"); "" for null.
export function relTime(value, now = Date.now()) {
    if (!value) return "";
    const s = Math.max(0, Math.round((now - new Date(value).getTime()) / 1000));
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
    return `${Math.floor(s / 86400)} d ago`;
}

// Shared number formatters (Cost & Machines, Simulations, Overview).
export const usd = (v) => `$${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const kt = (v) => `${((v ?? 0) / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })}K`;
// Seconds -> "2h 15m" / "4m 30s" / "45s".
export const dur = (s) => {
    s = Math.max(0, Math.floor(s || 0));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
    return h ? `${h}h ${m}m` : m ? `${m}m ${r}s` : `${r}s`;
};

// sx for cells/paragraphs that keep the author's line breaks.
export const preWrap = { whiteSpace: "pre-wrap", wordBreak: "break-word" };

// Canonical page-level selector bar (visual reference: Asset Inspector page).
// Use with size="small" SelectFields, minWidth 160.
export function SelectorBar({ children, sx }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, ...sx }}>
            {children}
        </Box>
    );
}

// FormControl + InputLabel + Select + MenuItems combo.
// onChange receives the selected VALUE, not the event.
// options: array of primitives, or objects { value, label, disabled }.
export function SelectField({ label, value, onChange, options, minWidth = 140, size, disabled, fullWidth, sx, children }) {
    return (
        <FormControl sx={{ minWidth, ...sx }} size={size} disabled={disabled} fullWidth={fullWidth}>
            <InputLabel>{label}</InputLabel>
            <Select value={value ?? ""} label={label} onChange={(e) => onChange(e.target.value)}>
                {children}
                {options && options.map(opt => {
                    const o = (typeof opt === "object" && opt !== null) ? opt : { value: opt, label: String(opt) };
                    return <MenuItem key={o.value} value={o.value} disabled={o.disabled}>{o.label}</MenuItem>;
                })}
            </Select>
        </FormControl>
    );
}

// Dialog + Title + Content + Cancel/Confirm actions.
export function FormDialog({
    open, title, onClose, onConfirm,
    confirmText = "OK", cancelText = "Cancel",
    confirmDisabled, cancelDisabled, hideConfirm,
    maxWidth, fullWidth, PaperProps, children
}) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth={fullWidth} PaperProps={PaperProps}>
            {title && <DialogTitle>{title}</DialogTitle>}
            <DialogContent>{children}</DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={cancelDisabled}>{cancelText}</Button>
                {!hideConfirm && (
                    <Button variant="contained" onClick={onConfirm} disabled={confirmDisabled}>{confirmText}</Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

// TableContainer + Paper + Table with column definitions and optional sorting.
// columns: [{ id, label, align, sortable, width, sx, render(row, index) }]  (width applies to the header cell, sx to every cell)
export function DataTable({ columns, rows, getRowKey, orderBy, order, onSort, sx }) {
    return (
        <TableContainer component={Paper} sx={sx}>
            <Table>
                <TableHead>
                    <TableRow>
                        {columns.map(col => (
                            <TableCell
                                key={col.id}
                                align={col.align}
                                sx={{ width: col.width, ...(col.sx || {}) }}
                                sortDirection={col.sortable && orderBy === col.id ? order : false}
                            >
                                {col.sortable ? (
                                    <TableSortLabel
                                        active={orderBy === col.id}
                                        direction={orderBy === col.id ? order : "asc"}
                                        onClick={() => onSort(col.id)}
                                    >
                                        {col.label}
                                    </TableSortLabel>
                                ) : col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, i) => (
                        <TableRow key={getRowKey ? getRowKey(row, i) : i}>
                            {columns.map(col => (
                                <TableCell key={col.id} align={col.align} sx={col.sx}>
                                    {col.render ? col.render(row, i) : row[col.id]}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
